import { HTTP } from '@awesome-cordova-plugins/http';
import { CapacitorWifiConnect, ConnectState } from '@falconeta/capacitor-wifi-connect';

import { IS_IOS } from '../../../../../common/constants';
import { timeout } from '../../../../../utils/common/common-utils';
import { BASE_IPV4_ADDRESS, WAIT_FOR_NATIVE_WIFI_MS } from './constants';

const DEVICE_REACHABILITY_TIMEOUT_MS = 5000;
const DEVICE_REACHABILITY_MAX_ATTEMPTS = 3;
const DEVICE_REACHABILITY_RETRY_DELAY_MS = 1000;

type WifiDeviceConnectOptions = {
  ssid: string;
  password: string;
};

// iOS hides the SSID when Precise Location is off; a reachable device API proves the join instead.
async function isDeviceApReachable() {
  for (let attempt = 0; attempt < DEVICE_REACHABILITY_MAX_ATTEMPTS; attempt++) {
    try {
      const response = await HTTP.sendRequest(`${BASE_IPV4_ADDRESS}/wifi_status.json`, {
        method: 'get',
        headers: { 'Content-type': 'application/json', Accept: 'application/json' },
        timeout: DEVICE_REACHABILITY_TIMEOUT_MS / 1000,
      });
      const data: { wifi_status?: unknown } = JSON.parse(response.data);
      if (data?.wifi_status) return true;
    } catch (error) {
      if (attempt === DEVICE_REACHABILITY_MAX_ATTEMPTS - 1) return false;
    }
    if (attempt < DEVICE_REACHABILITY_MAX_ATTEMPTS - 1) await timeout(DEVICE_REACHABILITY_RETRY_DELAY_MS);
  }
  return false;
}

export async function connectToSelectedNetwork(currentSsid: string, homeSsid: string, password?: string) {
  const isOnHomeSsid = currentSsid === homeSsid;

  // Force connect only if not already on the desired home network.
  if (!isOnHomeSsid) {
    const attemptConnect = async () => {
      if (password) {
        await CapacitorWifiConnect.secureConnect({
          ssid: homeSsid,
          password,
          saveNetwork: false,
        });
      } else {
        await CapacitorWifiConnect.connect({ ssid: homeSsid });
      }
    };

    try {
      await attemptConnect();
    } catch (e) {
      // Verify current SSID.
      const current = await CapacitorWifiConnect.getDeviceSSID().catch(() => undefined);
      const currentSsid = current?.value;
      if (currentSsid !== homeSsid) {
        await attemptConnect();
      }
    }

    await timeout(3000);
  }
}

export async function reconnectToHomeWifi(homeSsid: string, password: string) {
  if (!IS_IOS) {
    await CapacitorWifiConnect.disconnect();
    return;
  }

  // iOS disconnect() can't resolve the device WAP SSID, so it leaves the phone on the WAP.
  try {
    const current = await CapacitorWifiConnect.getDeviceSSID();
    await connectToSelectedNetwork(current?.value ?? '', homeSsid, password);
  } catch (e) {
    // Registration supports manual reconnection without repeating device-local setup.
    console.error(e);
  }
}

export async function connectToWifiDevice({ ssid, password }: WifiDeviceConnectOptions): Promise<ConnectState> {
  if (!IS_IOS) {
    const { value: connectState } = await CapacitorWifiConnect.secureConnect({ ssid, password });
    if (connectState === ConnectState.Ok) {
      await timeout(WAIT_FOR_NATIVE_WIFI_MS);
    }
    return connectState;
  }

  const current = await CapacitorWifiConnect.getDeviceSSID();
  await connectToSelectedNetwork(current?.value ?? '', ssid, password);

  const currentSsid = (await CapacitorWifiConnect.getDeviceSSID().catch(() => undefined))?.value;
  if (currentSsid === ssid) return ConnectState.Ok;
  if (currentSsid) return ConnectState.Ko;

  // SSID hidden: Ok if the device answers, otherwise surface a Precise Location hint.
  return (await isDeviceApReachable()) ? ConnectState.Ok : ConnectState.UnknowSsid;
}
